import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SPRINT_2_CONTENT } from '../data/course-data';

@Component({
  selector: 'app-sprint2-section',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-12">
      <!-- Section Header -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl relative overflow-hidden">
        <div class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-semibold mb-3 border border-[#F27D26]/30">
          <span class="material-symbols-outlined text-sm">hub</span>
          <span>SPRINT 2 (10h) • A INFRAESTRUTURA DE REDE</span>
        </div>
        <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-2">
          {{ content.title }}
        </h2>
        <p class="text-zinc-400 text-sm max-w-3xl">
          {{ content.subtitle }} — Entendendo a anatomia das startups versus PMEs tradicionais de telecomunicações, a atuação das incubadoras, aceleradoras e investidores, e os novos espaços colaborativos.
        </p>
      </div>

      <!-- Tabela Comparativa: Startup vs PME Tradicional -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-4">
          <div>
            <span class="text-xs font-mono font-semibold text-[#F27D26] uppercase tracking-wider block mb-1">
              PARADIGMA DE NEGÓCIOS EM REDES
            </span>
            <h3 class="text-2xl font-extrabold text-white">
              Entendendo a Startup: O Novo Padrão
            </h3>
            <p class="text-zinc-400 text-xs mt-1">
              Comparação estrutural entre a PME tradicional (ex: Provedor de Internet Regional) e a Startup de Tecnologia.
            </p>
          </div>

          <!-- Filter toggle -->
          <div class="flex items-center p-1 bg-[#0A0A0B] rounded-xl border border-white/10 text-xs font-medium">
            <button 
              type="button"
              (click)="selectedView.set('pme')"
              [class]="selectedView() === 'pme' ? 'bg-[#F27D26] text-black font-bold' : 'text-zinc-400 hover:text-white'"
              class="px-3 py-1.5 rounded-lg transition-all cursor-pointer">
              Foco PME
            </button>
            <button 
              type="button"
              (click)="selectedView.set('startup')"
              [class]="selectedView() === 'startup' ? 'bg-[#F27D26] text-black font-bold' : 'text-zinc-400 hover:text-white'"
              class="px-3 py-1.5 rounded-lg transition-all cursor-pointer">
              Foco Startup
            </button>
            <button 
              type="button"
              (click)="selectedView.set('all')"
              [class]="selectedView() === 'all' ? 'bg-[#F27D26] text-black font-bold' : 'text-zinc-400 hover:text-white'"
              class="px-3 py-1.5 rounded-lg transition-all cursor-pointer">
              Ver Tudo (Comparativo)
            </button>
          </div>
        </div>

        <!-- Table Grid -->
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="border-b border-white/10 text-xs font-mono text-zinc-400 uppercase bg-[#0A0A0B]">
                <th class="p-4 rounded-tl-xl">Variável</th>
                <th class="p-4 font-bold text-zinc-200">PME Tradicional (Ex: Provedor Local)</th>
                <th class="p-4 font-bold text-[#F27D26] bg-[#F27D26]/10 rounded-tr-xl">Startup de Tecnologia (Ex: SaaS/Sdn)</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-white/5 text-xs">
              @for (row of content.comparisonTable; track row.variable) {
                <tr class="hover:bg-white/[0.02] transition-colors">
                  <td class="p-4 font-bold text-white flex items-center space-x-2">
                    <span class="w-2 h-2 rounded-full bg-[#F27D26]"></span>
                    <span>{{ row.variable }}</span>
                  </td>
                  <td [class.opacity-30]="selectedView() === 'startup'" class="p-4 text-zinc-300 leading-relaxed transition-opacity">
                    {{ row.pme }}
                  </td>
                  <td [class.opacity-30]="selectedView() === 'pme'" class="p-4 font-semibold text-[#F27D26] bg-[#F27D26]/5 leading-relaxed transition-opacity">
                    {{ row.startup }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <!-- Agentes do Ecossistema -->
      <div class="bg-blueprint-grid rounded-2xl border border-white/10 p-6 sm:p-8 text-white shadow-xl space-y-8">
        <div>
          <span class="text-xs font-mono font-medium text-[#F27D26] uppercase tracking-wider block mb-1">
            • O FUNIL DE MATURAÇÃO DO NEGÓCIO
          </span>
          <h3 class="text-xl sm:text-2xl font-bold text-white flex items-center space-x-2">
            <span class="material-symbols-outlined text-[#F27D26]">account_tree</span>
            <span>Como a Rede se Apoia (Agentes do Ecossistema)</span>
          </h3>
          <p class="text-xs text-zinc-400 mt-1">Do laboratório acadêmico até a escala global: os agentes que sustentam as startups de TIC.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          @for (agent of content.ecosystemAgents; track agent.agent; let i = $index) {
            <div class="p-6 rounded-2xl bg-[#0A0A0B] border border-white/10 hover:border-[#F27D26]/50 transition-all space-y-4 relative group">
              <div class="flex items-center justify-between border-b border-white/10 pb-3">
                <span class="text-xs font-mono font-bold text-[#F27D26] uppercase">{{ agent.phase }}</span>
                <span class="w-8 h-8 rounded-lg bg-[#F27D26] text-black font-bold text-xs flex items-center justify-center">
                  0{{ i + 1 }}
                </span>
              </div>

              <div class="flex items-center space-x-3">
                <div class="w-12 h-12 rounded-xl bg-[#F27D26]/10 border border-[#F27D26]/30 flex items-center justify-center text-[#F27D26]">
                  <span class="material-symbols-outlined text-3xl">{{ agent.icon }}</span>
                </div>
                <h4 class="text-xl font-bold text-white group-hover:text-[#F27D26] transition-colors">{{ agent.agent }}</h4>
              </div>

              <p class="text-xs text-zinc-300 leading-relaxed">
                {{ agent.description }}
              </p>

              <div class="pt-2 text-[11px] font-mono text-zinc-400 flex items-center justify-between">
                <span>Nível de Risco: {{ i === 0 ? 'Conceitual' : i === 1 ? 'Validação' : 'Financeiro' }}</span>
                <span class="material-symbols-outlined text-sm text-[#F27D26]">trending_up</span>
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Novos Protocolos de Trabalho -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        @for (proto of content.workProtocols; track proto.title) {
          <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-4 hover:border-[#F27D26]/50 transition-all">
            <div class="flex items-center space-x-3">
              <div class="w-12 h-12 rounded-2xl bg-[#F27D26]/10 text-[#F27D26] border border-[#F27D26]/30 flex items-center justify-center">
                <span class="material-symbols-outlined text-3xl">{{ proto.icon }}</span>
              </div>
              <div>
                <h4 class="text-xl font-bold text-white">{{ proto.title }}</h4>
                <span class="text-xs font-mono text-[#F27D26] font-semibold uppercase">Novo Protocolo de Trabalho</span>
              </div>
            </div>

            <p class="text-xs text-zinc-300 leading-relaxed">
              {{ proto.description }}
            </p>
          </div>
        }
      </div>
    </section>
  `
})
export class Sprint2SectionComponent {
  content = SPRINT_2_CONTENT;
  selectedView = signal<'all' | 'pme' | 'startup'>('all');
}
